import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  CheckCircle2,
  Sparkles,
  GraduationCap,
  Briefcase,
  Palette,
  TrendingUp,
  XCircle,
  BookOpen,
  Zap,
  Layers,
  Workflow,
  Rocket,
  Calendar,
  Eye,
  Hammer,
  Hourglass,
  ShieldCheck,
  HandHeart,
} from "lucide-react";
import { LogoIcon, LogoWordmark, LogoFull } from "@/components/Logo";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

const audienceCards = [
  {
    icon: GraduationCap,
    title: "Beginners curious about AI",
    desc: "Start clear and confident — no jargon, no overwhelm.",
  },
  {
    icon: Briefcase,
    title: "Professionals who want to work faster",
    desc: "Reclaim hours every week using AI as a real productivity tool.",
  },
  {
    icon: Palette,
    title: "Creators & side hustlers",
    desc: "Generate ideas, content, and income streams powered by AI.",
  },
  {
    icon: TrendingUp,
    title: "Anyone future-proofing their skills",
    desc: "Stay relevant in a world that's moving toward AI everywhere.",
  },
];

const learnFeatures = [
  {
    icon: Sparkles,
    title: "AI Prompting Fundamentals",
    desc: "Master the C.R.A.F.T framework — Context, Role, Action, Format, Tone.",
  },
  {
    icon: Zap,
    title: "Time-saving AI workflows",
    desc: "Turn repetitive tasks into one-click systems.",
  },
  {
    icon: Workflow,
    title: "Real-world automation",
    desc: "Practical examples for work, business, and content creation.",
  },
  {
    icon: Layers,
    title: "Beginner → intermediate skills",
    desc: "A clear path that takes you from curious to capable.",
  },
];

const beforeBullets = [
  "Confused about AI",
  "Wasting time on tasks",
  "Not using AI effectively",
];

const afterBullets = [
  "Confident using AI daily",
  "Faster productivity",
  "Clear understanding of prompting systems",
  "Ability to build and experiment with AI tools",
];

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

const CHALLENGE_URL =
  "https://justin-s-school217.teachable.com/p/20-day-mastering-chatgpt-course";

function scrollTo(testId) {
  const el = document.querySelector(`[data-testid="${testId}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}

function focusInput(testId) {
  scrollTo(testId);
  setTimeout(() => {
    const el = document.querySelector(`[data-testid="${testId}"]`);
    if (el) el.focus({ preventScroll: true });
  }, 500);
}

/* -------------------------------------------------------------------------- */
/*  Lead capture form (Name + Email)                                          */
/* -------------------------------------------------------------------------- */

function LeadForm({ variant = "lead", idPrefix = "lead", showName = true }) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      toast.error("Please enter your email.");
      return;
    }
    if (showName && !firstName.trim()) {
      toast.error("Please enter your first name.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        first_name: firstName.trim() || "Friend",
        email: trimmedEmail,
        source: variant,
      });
      setSuccess(true);
      toast.success("Check your email for your free AI Starter Kit 🚀");
      setEmail("");
      setFirstName("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`${idPrefix}-form`}
      className="w-full max-w-xl mx-auto"
    >
      <div className="flex flex-col gap-3">
        {showName && (
          <input
            type="text"
            placeholder="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            data-testid={`${idPrefix}-name-input`}
            className="h-14 px-5 rounded-xl bg-slate-900/70 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-colors"
          />
        )}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={`${idPrefix}-email-input`}
            className="flex-1 min-w-0 h-14 px-5 rounded-xl bg-slate-900/70 border border-slate-700 text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/70 focus:ring-2 focus:ring-cyan-400/20 transition-colors"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid={`${idPrefix}-submit`}
            className="h-14 px-7 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-base tracking-tight disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap cta-glow"
          >
            {loading ? "Sending..." : success ? (
              <><CheckCircle2 className="w-5 h-5" />Sent</>
            ) : (
              <>Get Instant Access<ArrowRight className="w-5 h-5" /></>
            )}
          </button>
        </div>
      </div>
      <p
        className="mt-3 text-center text-sm text-cyan-300/80"
        data-testid={`${idPrefix}-reassurance`}
      >
        No spam. Just practical AI tools.
      </p>
    </form>
  );
}

/* -------------------------------------------------------------------------- */
/*  Header                                                                    */
/* -------------------------------------------------------------------------- */

function Header() {
  return (
    <header
      data-testid="header-nav"
      className="sticky top-0 z-50 backdrop-blur-xl bg-slate-950/70 border-b border-slate-800/60"
    >
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center">
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2.5">
          <LogoIcon size={30} data-testid="header-logo-icon" />
          <LogoWordmark size="sm" data-testid="header-logo-wordmark" />
        </a>
      </div>
    </header>
  );
}

/* -------------------------------------------------------------------------- */
/*  Hero                                                                      */
/* -------------------------------------------------------------------------- */

function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative overflow-hidden pt-14 pb-20 sm:pt-20 sm:pb-28"
    >
      {/* Background visuals */}
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-60" />
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-cyan-500/20 blur-3xl animate-float-orb" />
      <div className="absolute -bottom-40 -left-32 w-[500px] h-[500px] rounded-full bg-blue-600/20 blur-3xl animate-float-orb" />

      <div className="relative max-w-4xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <LogoFull
            maxWidth={420}
            className="w-[220px] sm:w-[340px] lg:w-[420px]"
            data-testid="hero-logo-full"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="flex justify-center mt-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
            <Sparkles className="w-3.5 h-3.5" />
            AI Prompting Mastery
          </div>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.25 }}
          data-testid="hero-headline"
          className="mt-8 text-center font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          Learn AI Skills That Save Time, Boost{" "}
          <span className="text-gradient-cyan">Productivity</span>, and Create
          New{" "}
          <span className="text-gradient-cyan">Income Opportunities</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.35 }}
          data-testid="hero-subheadline"
          className="mt-6 text-center text-slate-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed"
        >
          Beginner-friendly AI training and prompting systems designed for real
          people, not tech experts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.45 }}
          className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-3 sm:gap-4 max-w-2xl mx-auto"
        >
          <button
            type="button"
            onClick={() => scrollTo("course-section")}
            data-testid="hero-primary-cta"
            className="group flex-1 h-14 px-6 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-base tracking-tight flex items-center justify-center gap-2 cta-glow"
          >
            <span>Start Learning AI Today</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            type="button"
            onClick={() => focusInput("lead-name-input")}
            data-testid="hero-secondary-cta"
            className="group flex-1 h-14 px-6 rounded-xl border border-cyan-400/40 bg-slate-900/50 hover:bg-slate-900/80 hover:border-cyan-400/70 text-white font-heading font-bold text-base tracking-tight transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            Get Free AI Starter Kit
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Trust strip                                                               */
/* -------------------------------------------------------------------------- */

function TrustStrip() {
  return (
    <section
      data-testid="trust-strip"
      className="relative px-6 -mt-2 sm:-mt-6 pb-6"
    >
      <div className="max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-center gap-3 px-5 py-3 rounded-full border border-cyan-400/20 bg-slate-900/40 backdrop-blur-sm shadow-[0_10px_40px_-20px_rgba(34,211,238,0.35)]"
        >
          <span className="font-mono-brand text-[10px] sm:text-xs uppercase tracking-[0.2em] text-cyan-300">
            ✦
          </span>
          <p className="text-center text-slate-200 text-sm sm:text-base font-medium">
            Built in public.{" "}
            <span className="text-cyan-300">Real tools.</span> Real workflows.
            No fluff.
          </p>
          <span className="font-mono-brand text-[10px] sm:text-xs uppercase tracking-[0.2em] text-cyan-300">
            ✦
          </span>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Start Here — 3 onboarding cards                                           */
/* -------------------------------------------------------------------------- */

function StartHere() {
  const cards = [
    {
      icon: Rocket,
      title: "Free AI Starter Kit",
      tag: "Beginner Friendly",
      desc: "Start using AI in the next 10 minutes with simple prompts and tools.",
      button: "Get Free Access",
      action: () => focusInput("lead-name-input"),
      testId: "start-card-starter",
      featured: false,
    },
    {
      icon: BookOpen,
      title: "AI Prompting Mastery",
      tag: null,
      desc: "Build real AI skills step-by-step with practical prompting systems and workflows.",
      button: "Explore Course",
      action: () => scrollTo("course-section"),
      testId: "start-card-course",
      featured: true,
    },
    {
      icon: Calendar,
      title: "20-Day AI Challenge",
      tag: null,
      desc: "Learn AI daily with guided lessons designed to build confidence fast.",
      button: "Start Challenge",
      action: () => {
        window.location.href = CHALLENGE_URL;
      },
      testId: "start-card-challenge",
      featured: false,
    },
  ];

  return (
    <section
      data-testid="start-here-section"
      className="relative py-20 sm:py-24 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-6xl mx-auto">
        <SectionHeading
          eyebrow="Start Here"
          title="Start your AI journey here — choose your path:"
          accentWord="choose your path:"
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.45, delay: i * 0.1 }}
                data-testid={c.testId}
                className={`group relative flex flex-col p-7 rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                  c.featured
                    ? "md:scale-[1.04] bg-gradient-to-br from-cyan-500/10 via-slate-900/70 to-blue-600/5 border border-cyan-400/40 shadow-[0_20px_70px_-15px_rgba(34,211,238,0.45)] hover:shadow-[0_25px_80px_-10px_rgba(34,211,238,0.65)] hover:border-cyan-300/70"
                    : "bg-slate-900/40 border border-slate-800 hover:border-cyan-400/40 hover:bg-slate-900/70 hover:shadow-[0_15px_50px_-15px_rgba(34,211,238,0.35)]"
                }`}
              >
                {c.featured && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full border border-cyan-400/50 bg-slate-950 text-cyan-300 text-[10px] font-mono-brand uppercase tracking-[0.25em]">
                    Most Popular
                  </span>
                )}
                <div className="flex items-start justify-between gap-3">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                      c.featured
                        ? "bg-cyan-400/20 border border-cyan-400/50 text-cyan-200"
                        : "bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 group-hover:bg-cyan-400/20"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  {c.tag && (
                    <span className="px-2.5 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/30 text-cyan-300 text-[10px] font-mono-brand uppercase tracking-[0.2em] whitespace-nowrap">
                      {c.tag}
                    </span>
                  )}
                </div>
                <h3 className="mt-6 font-heading font-bold text-white text-xl sm:text-2xl tracking-tight leading-tight">
                  {c.title}
                </h3>
                <p className="mt-3 text-slate-400 text-sm sm:text-base leading-relaxed flex-1">
                  {c.desc}
                </p>
                <button
                  type="button"
                  onClick={c.action}
                  data-testid={`${c.testId}-button`}
                  className={`mt-7 group/btn h-12 px-5 rounded-xl font-heading font-bold text-sm tracking-tight inline-flex items-center justify-center gap-2 transition-all duration-200 ${
                    c.featured
                      ? "bg-cyan-400 hover:bg-cyan-300 text-slate-950 cta-glow"
                      : "border border-cyan-400/40 hover:border-cyan-300/80 bg-slate-900/60 hover:bg-slate-900 text-white hover:-translate-y-0.5"
                  }`}
                >
                  {c.button}
                  <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" />
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section heading helper                                                    */
/* -------------------------------------------------------------------------- */

function SectionHeading({ eyebrow, title, subtitle, accentWord }) {
  const titleNode = accentWord
    ? title.split(accentWord).reduce((acc, part, i, arr) => {
        if (i < arr.length - 1) {
          return [
            ...acc,
            part,
            <span key={i} className="text-gradient-cyan">
              {accentWord}
            </span>,
          ];
        }
        return [...acc, part];
      }, [])
    : title;

  return (
    <div className="text-center max-w-2xl mx-auto">
      {eyebrow && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
          <Sparkles className="w-3.5 h-3.5" />
          {eyebrow}
        </div>
      )}
      <motion.h2
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.5 }}
        className="mt-6 font-heading font-bold text-white text-3xl sm:text-5xl leading-[1.1] tracking-tight"
      >
        {titleNode}
      </motion.h2>
      {subtitle && (
        <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Who This Is For                                                           */
/* -------------------------------------------------------------------------- */

function WhoThisIsFor() {
  return (
    <section
      data-testid="who-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="Who It's For"
          title="Who This Is For"
          accentWord="For"
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {audienceCards.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`audience-card-${i}`}
                className="group p-6 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-cyan-400/40 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 group-hover:bg-cyan-400/20 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading font-semibold text-white text-base leading-snug">
                  {card.title}
                </h3>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  What You'll Learn                                                         */
/* -------------------------------------------------------------------------- */

function WhatYoullLearn() {
  return (
    <section
      data-testid="learn-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="What You'll Learn"
          title="Practical AI skills, simplified."
          accentWord="simplified."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {learnFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid={`learn-feature-${i}`}
                className="group p-6 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-cyan-400/40 hover:bg-slate-900/70 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 group-hover:bg-cyan-400/20 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-white text-lg leading-snug">
                      {f.title}
                    </h3>
                    <p className="mt-2 text-slate-400 text-sm sm:text-base leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Transformation                                                            */
/* -------------------------------------------------------------------------- */

function Transformation() {
  return (
    <section
      data-testid="transformation-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="The Transformation"
          title="From Confused to Confident"
          accentWord="Confident"
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
            data-testid="before-card"
            className="p-6 sm:p-8 rounded-2xl bg-slate-900/40 border border-slate-800"
          >
            <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-slate-500">
              Before
            </span>
            <h3 className="mt-3 font-heading font-bold text-slate-300 text-2xl">
              Stuck & guessing
            </h3>
            <ul className="mt-6 flex flex-col gap-3">
              {beforeBullets.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-slate-400 text-base"
                >
                  <XCircle className="w-5 h-5 text-rose-400/80 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="after-card"
            className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-slate-900/60 to-blue-600/5 border border-cyan-400/40 shadow-[0_10px_60px_-20px_rgba(34,211,238,0.4)]"
          >
            <span className="font-mono-brand text-xs uppercase tracking-[0.2em] text-cyan-300">
              After
            </span>
            <h3 className="mt-3 font-heading font-bold text-white text-2xl">
              In control & creating
            </h3>
            <ul className="mt-6 flex flex-col gap-3">
              {afterBullets.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 text-slate-100 text-base"
                >
                  <CheckCircle2 className="w-5 h-5 text-cyan-300 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Built in Public                                                           */
/* -------------------------------------------------------------------------- */

const builtInPublicBullets = [
  { icon: Rocket, text: "Real progress, not theory" },
  { icon: Eye, text: "Transparent learning process" },
  { icon: Hammer, text: "Built using the same AI tools I teach" },
];

function BuiltInPublic() {
  return (
    <section
      data-testid="built-in-public-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="Built in Public"
          title="Built in Public — Follow the Journey"
          accentWord="Follow the Journey"
        />
        <div className="mt-12 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5 }}
            className="relative p-7 sm:p-9 rounded-2xl bg-slate-900/40 border border-slate-800"
          >
            <span className="absolute -left-px top-8 bottom-8 w-1 bg-gradient-to-b from-cyan-400 via-cyan-400/40 to-transparent rounded-r" />
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              I'm actively building JK Academy in real-time — sharing the
              process, testing ideas, and improving based on real feedback.
            </p>
            <ul className="mt-6 space-y-3">
              {builtInPublicBullets.map((b, i) => {
                const Icon = b.icon;
                return (
                  <li
                    key={b.text}
                    data-testid={`bip-bullet-${i}`}
                    className="flex items-start gap-3 text-slate-200 text-sm sm:text-base"
                  >
                    <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="pt-1.5">{b.text}</span>
                  </li>
                );
              })}
            </ul>
          </motion.div>

          {/* Build timeline */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            data-testid="build-timeline"
            className="hidden md:block w-56 p-5 rounded-2xl bg-slate-900/60 border border-cyan-400/25 shadow-[0_15px_50px_-20px_rgba(34,211,238,0.4)]"
          >
            <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-cyan-300">
              Build Log
            </p>
            <ol className="mt-4 space-y-3">
              {[
                { label: "v0.1 · Idea", done: true },
                { label: "v0.2 · Landing live", done: true },
                { label: "v0.3 · Starter Kit", done: true },
                { label: "v0.4 · Course beta", done: false },
                { label: "v1.0 · You inside", done: false },
              ].map((step, i) => (
                <li key={step.label} className="flex items-center gap-3">
                  <span
                    className={`flex-shrink-0 w-3 h-3 rounded-full ring-2 ${
                      step.done
                        ? "bg-cyan-400 ring-cyan-400/30 shadow-[0_0_10px_rgba(34,211,238,0.7)]"
                        : "bg-slate-700 ring-slate-700/30"
                    }`}
                  />
                  <span
                    className={`text-xs ${
                      step.done ? "text-slate-200" : "text-slate-500"
                    }`}
                  >
                    {step.label}
                  </span>
                  {i < 4 && (
                    <span className="ml-auto text-[10px] text-slate-600">
                      {step.done ? "✓" : "·"}
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Micro-social proof                                                        */
/* -------------------------------------------------------------------------- */

const microProofBullets = [
  { icon: Sparkles, text: "Simple, beginner-friendly approach" },
  { icon: ShieldCheck, text: "No tech background required" },
  { icon: Workflow, text: "Focus on real-world use" },
  { icon: Hammer, text: "Learning by doing, not just watching" },
];

function MicroProof() {
  return (
    <section
      data-testid="micro-proof-section"
      className="relative py-20 sm:py-24 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <SectionHeading
          eyebrow="What Draws People In"
          title="Why People Are Starting with JK Academy"
          accentWord="JK Academy"
        />
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {microProofBullets.map((b, i) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.text}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                data-testid={`micro-proof-${i}`}
                className="group p-5 rounded-2xl bg-slate-900/40 border border-slate-800 hover:border-cyan-400/40 hover:bg-slate-900/70 transition-all duration-300 text-center"
              >
                <div className="mx-auto w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center text-cyan-300 group-hover:bg-cyan-400/20 transition-colors">
                  <Icon className="w-5 h-5" />
                </div>
                <p className="mt-4 text-slate-200 text-sm leading-relaxed">
                  {b.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  20-Day ChatGPT Challenge                                                  */
/* -------------------------------------------------------------------------- */

const challengeBullets = [
  "Daily beginner-friendly lessons",
  "Real prompts & workflows",
  "Hands-on AI practice",
  "Build confidence using ChatGPT",
  "Designed for busy beginners",
];

function Challenge() {
  return (
    <section
      data-testid="challenge-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/40 p-8 sm:p-12 shadow-[0_20px_80px_-20px_rgba(34,211,238,0.4)]"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-blue-600/15 blur-3xl" />

          <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
                <Rocket className="w-3.5 h-3.5" />
                Free 20-Day Challenge
              </div>
              <h2
                data-testid="challenge-headline"
                className="mt-5 font-heading font-extrabold text-white text-3xl sm:text-5xl leading-[1.05] tracking-tight"
              >
                🚀 Free 20-Day{" "}
                <span className="text-gradient-cyan">ChatGPT Challenge</span>
              </h2>
              <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">
                Learn practical AI skills one step at a time.
              </p>
              <ul className="mt-6 space-y-2.5">
                {challengeBullets.map((item, i) => (
                  <li
                    key={item}
                    data-testid={`challenge-bullet-${i}`}
                    className="flex items-start gap-3 text-slate-200 text-sm sm:text-base"
                  >
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-slate-400 text-sm sm:text-base leading-relaxed max-w-xl">
                In just a few minutes a day, you'll start learning how to use AI
                for productivity, creativity, business, and everyday life.
              </p>
              <a
                href={CHALLENGE_URL}
                data-testid="challenge-cta"
                className="mt-8 group h-14 px-8 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-base tracking-tight inline-flex items-center justify-center gap-2 cta-glow no-underline"
              >
                <span>👉</span>
                Start the Free Challenge
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </a>
              <a
                href={CHALLENGE_URL}
                data-testid="challenge-secondary-link"
                className="mt-4 inline-block text-cyan-300/80 hover:text-cyan-200 text-sm font-medium underline decoration-cyan-400/40 hover:decoration-cyan-300/80 underline-offset-4 transition-colors"
              >
                Already have the Starter Kit? Begin the Challenge Here.
              </a>
            </div>

            {/* 4 × 5 day grid visual */}
            <div className="hidden md:flex items-center justify-center">
              <div
                data-testid="challenge-grid"
                className="relative p-4 rounded-2xl bg-slate-950/60 border border-cyan-400/25 shadow-[0_15px_50px_-20px_rgba(34,211,238,0.45)]"
              >
                <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-cyan-300 text-center mb-3">
                  20 Days
                </p>
                <div className="grid grid-cols-4 gap-1.5">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div
                      key={i}
                      className="w-10 h-10 rounded-md bg-slate-900/80 border border-slate-800 flex items-center justify-center text-[10px] font-mono-brand text-slate-500 hover:border-cyan-400/50 hover:text-cyan-300 hover:bg-cyan-400/5 transition-colors"
                      style={{
                        animationDelay: `${i * 60}ms`,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                  ))}
                </div>
                <p className="mt-3 text-center font-mono-brand text-[9px] uppercase tracking-[0.25em] text-slate-500">
                  One small win per day
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Founder Note (inline callout)                                             */
/* -------------------------------------------------------------------------- */

function FounderNote() {
  return (
    <div
      data-testid="founder-note"
      className="relative max-w-3xl mx-auto px-6 pt-8 pb-4"
    >
      <div className="relative p-6 sm:p-7 rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-blue-950/30 shadow-[0_10px_50px_-20px_rgba(34,211,238,0.35)]">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center text-cyan-300">
            <HandHeart className="w-5 h-5" />
          </div>
          <div>
            <p className="font-mono-brand text-[10px] uppercase tracking-[0.25em] text-cyan-300">
              Founder Note
            </p>
            <p className="mt-2 text-slate-200 text-base sm:text-lg leading-relaxed">
              This is an early-stage platform designed for real people learning
              AI from scratch.{" "}
              <span className="text-white font-semibold">
                If you're here now, you're ahead of the curve.
              </span>
            </p>
            <p className="mt-3 text-slate-400 text-sm">
              Founding members will grow with the platform as it evolves.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Why JK Academy                                                            */
/* -------------------------------------------------------------------------- */

const whyBullets = [
  "Built in public (real journey, real results)",
  "Beginner-first approach",
  "Practical, real-world AI workflows",
  "No hype or fake guru tactics",
  "Learn by building, not just watching",
];

function WhyJKAcademy() {
  return (
    <section
      data-testid="why-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-4xl mx-auto">
        <SectionHeading
          eyebrow="Why JK Academy"
          title="Why JK Academy?"
          accentWord="JK Academy?"
        />
        <ul className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {whyBullets.map((item, i) => (
            <motion.li
              key={item}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              data-testid={`why-bullet-${i}`}
              className="group flex items-start gap-4 p-5 rounded-xl bg-slate-900/40 border border-slate-800 hover:border-cyan-400/40 hover:bg-slate-900/70 transition-all duration-300"
            >
              <span className="flex-shrink-0 w-9 h-9 rounded-lg bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-cyan-300" />
              </span>
              <span className="text-slate-200 text-base sm:text-lg leading-relaxed pt-1">
                {item}
              </span>
            </motion.li>
          ))}
        </ul>
        <div className="mt-10 flex justify-center">
          <button
            type="button"
            onClick={() => focusInput("lead-name-input")}
            data-testid="why-cta"
            className="group h-12 px-7 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-sm tracking-tight flex items-center justify-center gap-2 cta-glow"
          >
            Get the Free Starter Kit
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Course Preview                                                            */
/* -------------------------------------------------------------------------- */

function CoursePreview() {
  const handleJoin = () => {
    toast.info(
      "AI Prompting Mastery enrollment opens soon. Grab the free Starter Kit now!",
    );
    focusInput("lead-name-input");
  };

  return (
    <section
      data-testid="course-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6 }}
          className="relative overflow-hidden rounded-3xl border border-cyan-400/30 bg-gradient-to-br from-slate-900 via-slate-900 to-blue-950/40 p-8 sm:p-12 shadow-[0_20px_80px_-20px_rgba(34,211,238,0.35)]"
        >
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-cyan-500/15 blur-3xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
                <BookOpen className="w-3.5 h-3.5" />
                The Course
              </div>
              <h2
                data-testid="course-headline"
                className="mt-5 font-heading font-bold text-white text-3xl sm:text-5xl leading-[1.05] tracking-tight"
              >
                <span className="text-gradient-cyan">AI Prompting</span> Mastery
              </h2>
              <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed max-w-xl">
                A beginner-friendly, practical course teaching real-world AI
                prompting and workflows you can apply immediately — for work,
                business, and side projects.
              </p>
              <ul className="mt-6 space-y-2.5">
                {[
                  "Beginner-friendly · no tech skills required",
                  "Practical, real-world AI prompting & automation",
                  "Step-by-step path from curious to capable",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-3 text-slate-200 text-sm sm:text-base"
                  >
                    <CheckCircle2 className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={handleJoin}
                data-testid="course-cta"
                className="mt-8 group h-14 px-8 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-base tracking-tight inline-flex items-center justify-center gap-2 cta-glow"
              >
                Join JK Academy
                <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
            <div className="hidden md:flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-cyan-400/25 blur-3xl scale-125" />
                <LogoIcon size={150} glow />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Lead Capture                                                              */
/* -------------------------------------------------------------------------- */

function LeadCapture() {
  return (
    <section
      data-testid="lead-section"
      className="relative py-20 sm:py-28 px-6 border-t border-slate-800/60"
    >
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-40" />
      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
          <Sparkles className="w-3.5 h-3.5" />
          Free Starter Kit
        </div>
        <h2
          data-testid="lead-headline"
          className="mt-6 font-heading font-bold text-white text-3xl sm:text-5xl leading-[1.05] tracking-tight"
        >
          Get the{" "}
          <span className="text-gradient-cyan">Free AI Starter Kit</span>
        </h2>
        <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
          Beginner-friendly prompts, workflows, and real examples — delivered
          to your inbox.
        </p>
        <p
          className="mt-3 inline-flex items-center gap-2 text-cyan-300 text-sm font-medium"
          data-testid="lead-urgency"
        >
          <Zap className="w-4 h-4" />
          Instant access — start learning today
        </p>
        <div className="mt-10">
          <LeadForm variant="lead-capture" idPrefix="lead" showName={true} />
        </div>

        {/* Risk-reducer badges */}
        <div
          data-testid="risk-reducers"
          className="mt-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3"
        >
          {[
            { icon: CheckCircle2, label: "No experience needed" },
            { icon: Hourglass, label: "Start at your own pace" },
            { icon: ShieldCheck, label: "No spam. Just real AI tools." },
          ].map((b) => {
            const Icon = b.icon;
            return (
              <span
                key={b.label}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/60 border border-slate-700 text-slate-300 text-xs sm:text-sm"
              >
                <Icon className="w-3.5 h-3.5 text-cyan-300" />
                {b.label}
              </span>
            );
          })}
        </div>

        <p
          data-testid="lead-soft-urgency"
          className="mt-5 text-cyan-300/80 text-sm font-medium"
        >
          Start now and stay ahead as AI continues to grow.
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  About / Builder Story (short)                                             */
/* -------------------------------------------------------------------------- */

function AboutBuilder() {
  return (
    <section
      data-testid="about-section"
      className="relative py-20 sm:py-24 px-6 border-t border-slate-800/60"
    >
      <div className="relative max-w-4xl mx-auto">
        <SectionHeading
          eyebrow="The Builder"
          title="Built in real-time, by someone learning alongside you."
          accentWord="learning alongside you."
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="mt-12 grid md:grid-cols-[auto_1fr] gap-8 items-center"
        >
          <div className="group relative mx-auto md:mx-0">
            <div className="absolute -inset-4 rounded-[2rem] animate-founder-aura pointer-events-none" />
            <div className="absolute -inset-3 bg-cyan-400/20 blur-3xl rounded-full pointer-events-none" />
            <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-2xl overflow-hidden border-2 border-cyan-400/60 shadow-[0_20px_60px_-10px_rgba(34,211,238,0.5)] group-hover:scale-[1.02] transition-transform duration-300">
              <img
                src="https://customer-assets.emergentagent.com/job_prompt-mastery-58/artifacts/fu0b679g_1000013191.jpg"
                alt="Justin May — Founder, JK Academy"
                className="w-full h-full object-cover"
                draggable={false}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/40 to-transparent" />
            </div>
          </div>

          <div className="relative p-6 sm:p-8 rounded-2xl bg-slate-900/40 border border-slate-800">
            <span className="absolute -left-px top-6 bottom-6 w-1 bg-gradient-to-b from-cyan-400 via-cyan-400/40 to-transparent rounded-r" />
            <div className="space-y-4 text-slate-300 text-base sm:text-lg leading-relaxed">
              <p>
                I'm{" "}
                <span className="text-white font-medium">Justin May</span> —
                building JK Academy in real-time while learning AI alongside
                you.
              </p>
              <p>
                JK Academy isn't built on hype. It's built on real tools, real
                workflows, and the experiments I'm running every week. What
                works, you learn.
              </p>
            </div>
            <div className="mt-6 pt-5 border-t border-slate-800 flex items-center gap-3">
              <LogoIcon size={26} />
              <p className="font-mono-brand text-[11px] uppercase tracking-[0.2em] text-cyan-300/90">
                — Justin May, Founder of JK Academy
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Final CTA                                                                 */
/* -------------------------------------------------------------------------- */

function FinalCta() {
  return (
    <section
      data-testid="final-cta-section"
      className="relative py-28 sm:py-36 px-6 border-t border-slate-800/60 overflow-hidden"
    >
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-cyan-500/10 blur-3xl" />
      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300 text-xs font-mono-brand uppercase tracking-[0.2em]">
          <Sparkles className="w-3.5 h-3.5" />
          Start Today · Free
        </div>
        <h2
          data-testid="final-cta-headline"
          className="mt-6 font-heading font-extrabold text-white text-3xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
        >
          Start learning AI today and{" "}
          <span className="text-gradient-cyan">build skills that matter.</span>
        </h2>
        <p className="mt-6 text-slate-300 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
          Join JK Academy and pick up the free Starter Kit on the way in.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-stretch justify-center gap-3 sm:gap-4 max-w-2xl mx-auto">
          <button
            type="button"
            onClick={() => scrollTo("course-section")}
            data-testid="final-primary-cta"
            className="group flex-1 h-16 px-8 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-heading font-bold text-lg tracking-tight flex items-center justify-center gap-2 cta-glow"
          >
            Start Learning AI Today
            <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
          </button>
          <button
            type="button"
            onClick={() => focusInput("lead-name-input")}
            data-testid="final-secondary-cta"
            className="group flex-1 h-16 px-8 rounded-xl border border-cyan-400/40 bg-slate-900/50 hover:bg-slate-900/80 hover:border-cyan-400/70 text-white font-heading font-bold text-lg tracking-tight transition-all duration-200 hover:-translate-y-0.5 flex items-center justify-center gap-2"
          >
            Get Free AI Starter Kit
          </button>
        </div>
        <p
          data-testid="final-soft-urgency"
          className="mt-6 text-cyan-300/80 text-sm font-medium"
        >
          Start now and stay ahead as AI continues to grow.
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Footer                                                                    */
/* -------------------------------------------------------------------------- */

function Footer() {
  return (
    <footer
      data-testid="footer"
      className="border-t border-slate-800/60 py-10 px-6"
    >
      <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
        <div className="flex items-center gap-2.5">
          <LogoIcon size={24} data-testid="footer-logo-icon" />
          <span className="font-mono-brand text-xs uppercase tracking-[0.2em]">
            JK Academy © {new Date().getFullYear()}
          </span>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
          <a
            href="mailto:justin.jkacademy@gmail.com"
            data-testid="footer-contact-email"
            className="font-mono-brand text-xs uppercase tracking-[0.2em] hover:text-cyan-300 transition-colors"
          >
            justin.jkacademy@gmail.com
          </a>
          <span className="font-mono-brand text-xs uppercase tracking-[0.2em]">
            Learn AI. Work smarter.
          </span>
        </div>
      </div>
    </footer>
  );
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-950 text-white antialiased">
      <Header />
      <main>
        <Hero />
        <TrustStrip />
        <StartHere />
        <WhoThisIsFor />
        <WhatYoullLearn />
        <Transformation />
        <WhyJKAcademy />
        <BuiltInPublic />
        <MicroProof />
        <CoursePreview />
        <Challenge />
        <FounderNote />
        <LeadCapture />
        <AboutBuilder />
        <FinalCta />
      </main>
      <Footer />
    </div>
  );
}
